/**
 * chatDraftShipment — translate the chat-extracted state object into the
 * shipment shape ShipmentRail consumes, so the right rail mirrors what Marq
 * extracted while the user is still in the chat panel (P0-5).
 *
 * The chat's `state` is a flat dictionary keyed by camelCase fields
 * (originPort, destinationPort, containerType, etc.). The rail expects nested
 * route/cargo/commercial/commodity sections.
 */

import { normaliseYMD } from './preview/dateHelpers'

const EMPTY_DASH = '—'

function toCode(name) {
  if (!name) return EMPTY_DASH
  return String(name).slice(0, 5).toUpperCase()
}

function composeLocation(parts) {
  return parts.filter(Boolean).map(s => String(s).trim()).filter(Boolean).join(', ')
}

export function buildChatDraftShipment(state = {}) {
  // The chat extracts origin/destination under different keys depending on
  // mode: ocean uses originPort/destinationPort; trucking uses
  // pickupAddress/destinationAddress (with city/state/zip aliases). Mirror the
  // backend's normalizer fall-through so the rail shows the same value the
  // header chips already display.
  const origin = state.originPort
    || state.pickupAddress
    || state.pickupDisplay
    || composeLocation([state.pickupCity, state.pickupState, state.pickupZip])
    || state.originAddress
    || composeLocation([state.originCity, state.originState, state.originZip])
    || state.pickupLocation
    || state.originLocation
    || ''
  const destination = state.destinationPort
    || state.destinationAddress
    || state.destinationAddressLine
    || composeLocation([state.destinationCity, state.destinationState, state.destinationZip])
    || state.deliveryAddress
    || composeLocation([state.deliveryCity, state.deliveryState, state.deliveryZip])
    || state.destinationLocation
    || state.deliveryLocation
    || ''
  const containerType = state.containerType || null
  const containerCount = state.containerCount || null
  const mode = containerType
    ? `Ocean ${containerType}` + (containerCount && containerCount > 1 ? ` ×${containerCount}` : '')
    : state.truckType || EMPTY_DASH

  return {
    id: EMPTY_DASH,
    status: 'DRAFT',
    route: {
      origin: { code: toCode(origin), name: origin || EMPTY_DASH },
      destination: { code: toCode(destination), name: destination || EMPTY_DASH }
    },
    mode,
    cargo: {
      containerType,
      containerCount,
      // Trucking drafts carry weight in lb; ocean/air in kg. Keep the value
      // numeric so the rail's display formatter (Number().toLocaleString())
      // can render it — embedding the unit in the string would yield NaN.
      totalWeight: state.totalWeight != null ? Number(state.totalWeight) : null,
      totalWeightUnit: state.truckType ? 'lb' : 'kg',
      palletCount: null,
      dims: null
    },
    commodity: {
      description: state.commodity || null
    },
    commercial: {
      incoterm: state.incoterm || null
    },
    // The LLM emits the date under whichever field name fits the user's
    // phrasing — cargoReadyDate / pickupDate / pickupOn / deliveryDeadline.
    // The backend's trucking normalizer collapses pickup* into
    // cargoReadyDate, but only when it recognizes the turn as trucking-
    // shaped, and never touches deliveryDeadline. Fall through the aliases
    // so the rail mirrors whatever date came back from the chat.
    readyDate: normaliseYMD(
      state.cargoReadyDate
        || state.pickupDate
        || state.pickupOn
        || state.deliveryDeadline
    ),
    providers: [],
    timeline: [{ date: '', label: 'Draft', current: true }]
  }
}
